import { useEffect, useRef, useState } from "react";
import { ExternalLink, ShoppingBag, MapPin } from "lucide-react";
import api from "@/lib/api";
import Skeleton from "@/components/Skeleton";

// Module-level in-memory cache shared across mounts. Keyed by `${query}|${limit}`.
const memCache = new Map();
// null = unknown, true/false = backend has told us whether SERPAPI_KEY is set.
let productsEnabled = null;

function cacheKey(q, limit) {
  return `${(q || "").toLowerCase().trim()}|${limit}`;
}

function GridSkeleton() {
  return (
    <div className="rsf-grid" data-testid="real-finds-loading">
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="rsf-card skeleton" aria-hidden>
          <Skeleton w="100%" h={130} r={12} />
          <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "10px 10px 12px 10px" }}>
            <Skeleton w="90%" h={10} />
            <Skeleton w="60%" h={10} />
            <Skeleton w="45%" h={12} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Pick a soft brand color per retailer to tint the fallback thumb.
const RETAILER_BRAND = {
  "Google Shopping": { bg: "#fff", fg: "#4285f4", letter: "G" },
  "Amazon":          { bg: "#232f3e", fg: "#ff9900", letter: "a" },
  "Zara":            { bg: "#fff", fg: "#0a0a0a", letter: "ZARA" },
  "H&M":             { bg: "#e50010", fg: "#fff", letter: "H&M" },
  "Uniqlo":          { bg: "#e50012", fg: "#fff", letter: "UQ" },
  "ASOS":            { bg: "#0a0a0a", fg: "#fff", letter: "ASOS" },
  "PacSun":          { bg: "#fff", fg: "#0a0a0a", letter: "PS" },
  "Urban Outfitters":{ bg: "#0a0a0a", fg: "#fff", letter: "UO" },
  "SSENSE":          { bg: "#0a0a0a", fg: "#fff", letter: "S" },
  "END.":            { bg: "#fff", fg: "#0a0a0a", letter: "END." },
  "Nordstrom":       { bg: "#fff", fg: "#0a0a0a", letter: "N" },
  "Macy's":          { bg: "#c8102e", fg: "#fff", letter: "★" },
  "Target":          { bg: "#cc0000", fg: "#fff", letter: "◎" },
  "Walmart":         { bg: "#0071dc", fg: "#fff", letter: "✦" },
  "Nike":            { bg: "#0a0a0a", fg: "#fff", letter: "✓" },
  "Adidas":          { bg: "#0a0a0a", fg: "#fff", letter: "▲" },
  "Levi's":          { bg: "#b80000", fg: "#fff", letter: "L" },
  "Lululemon":       { bg: "#cb333b", fg: "#fff", letter: "Ω" },
  "StockX":          { bg: "#006340", fg: "#fff", letter: "X" },
  "GOAT":            { bg: "#fff", fg: "#0a0a0a", letter: "GOAT" },
  "Foot Locker":     { bg: "#000", fg: "#fff", letter: "FL" },
  "Banana Republic": { bg: "#0a0a0a", fg: "#d4ad6f", letter: "BR" },
  "J.Crew":          { bg: "#0a0a0a", fg: "#fff", letter: "J" },
  "COS":             { bg: "#fff", fg: "#0a0a0a", letter: "COS" },
  "Everlane":        { bg: "#fff", fg: "#0a0a0a", letter: "E" },
  "Ralph Lauren":    { bg: "#0a274f", fg: "#fff", letter: "RL" },
  "Brooks Brothers": { bg: "#0a274f", fg: "#fff", letter: "BB" },
  "MR PORTER":       { bg: "#fff", fg: "#0a0a0a", letter: "MR P" },
  "NET-A-PORTER":    { bg: "#fff", fg: "#0a0a0a", letter: "NAP" },
  "Farfetch":        { bg: "#fff", fg: "#0a0a0a", letter: "FF" },
  "Allen Edmonds":   { bg: "#3a2418", fg: "#d4ad6f", letter: "AE" },
  "Cole Haan":       { bg: "#3a2418", fg: "#fff", letter: "CH" },
  "SuitSupply":      { bg: "#0a0a0a", fg: "#fff", letter: "SS" },
  "Warby Parker":    { bg: "#fff", fg: "#0a0a0a", letter: "WP" },
  "Sunglass Hut":    { bg: "#fff", fg: "#0a0a0a", letter: "SH" },
};

function FallbackCard({ retailer, query, dt }) {
  const brand = RETAILER_BRAND[retailer.name] || { bg: "#1a1a1a", fg: "#fff", letter: retailer.name[0] };
  return (
    <a
      className="rsf-card fallback"
      href={retailer.url}
      target="_blank"
      rel="noreferrer noopener"
      data-testid={dt}
      title={`Search ${retailer.name} for "${query}"`}
    >
      <div className="rsf-thumb retailer-tile" style={{ background: brand.bg, color: brand.fg }}>
        <div className="retailer-mark" style={{ color: brand.fg }}>{brand.letter}</div>
        {retailer.in_person && (
          <div className="rsf-instore-badge">
            <MapPin size={9} strokeWidth={2.2} /> ALSO IN-STORE
          </div>
        )}
      </div>
      <div className="rsf-body">
        <div className="rsf-title">Browse {retailer.name}</div>
        <div className="rsf-meta">
          <span className="rsf-source">{retailer.name}</span>
        </div>
        <div className="rsf-cta">
          Shop now <ExternalLink size={11} style={{ marginLeft: 4 }} />
        </div>
      </div>
    </a>
  );
}

function ProductCard({ product, dt }) {
  return (
    <a
      className="rsf-card"
      href={product.product_link || "#"}
      target="_blank"
      rel="noreferrer noopener"
      data-testid={dt}
      title={product.title}
    >
      <div className="rsf-thumb">
        {product.image_url ? (
          <img
            src={product.image_url}
            alt={product.title}
            loading="lazy"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <div className="rsf-thumb-placeholder"><ShoppingBag size={24} strokeWidth={1.4} /></div>
        )}
      </div>
      <div className="rsf-body">
        <div className="rsf-title">{product.title}</div>
        <div className="rsf-meta">
          <span className="rsf-source">{product.source}</span>
          {product.price && <span className="rsf-price numeric">{product.price}</span>}
        </div>
        <div className="rsf-cta">
          Shop now <ExternalLink size={11} style={{ marginLeft: 4 }} />
        </div>
      </div>
    </a>
  );
}

/**
 * Google-Shopping-style grid of product cards.
 *
 * Props:
 *   query             — search string (required)
 *   limit             — max products to show (default 6)
 *   testId            — optional data-testid prefix
 *   fallbackRetailers — retailers to render when SerpAPI is not configured
 *                       (each: {name, url, in_person, tier}). Backend-supplied.
 *   sectionLabel      — optional override for the section eyebrow
 */
export default function RealStoreFinds({
  query,
  limit = 6,
  testId,
  fallbackRetailers,
  sectionLabel,
}) {
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState(null);
  const [disabled, setDisabled] = useState(false);
  const ranRef = useRef(false);
  const cleanQuery = (query || "").trim();
  const key = cacheKey(cleanQuery, limit);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    if (!cleanQuery) return;

    if (productsEnabled === false) {
      setDisabled(true);
      setProducts([]);
      return;
    }
    if (memCache.has(key)) {
      setProducts(memCache.get(key));
      if (productsEnabled === false) setDisabled(true);
      return;
    }

    const run = async () => {
      setLoading(true);
      try {
        const { data } = await api.post("/products/search", {
          queries: [cleanQuery],
          limit_per_query: limit,
        });
        if (data?.enabled === false) {
          productsEnabled = false;
          setDisabled(true);
          memCache.set(key, []);
          setProducts([]);
          return;
        }
        productsEnabled = true;
        const found = data?.results?.[cleanQuery] || [];
        memCache.set(key, found);
        setProducts(found);
      } catch {
        productsEnabled = false;
        setDisabled(true);
        memCache.set(key, []);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [key, cleanQuery, limit]);

  if (!cleanQuery) return null;
  if (loading) return <GridSkeleton />;

  const hasRealProducts = products && products.length > 0;
  const showFallback = disabled || (products !== null && products.length === 0);

  // Retailers for fallback: prefer per-item list from backend; else use a sane default
  const fallbacks = (fallbackRetailers && fallbackRetailers.length > 0)
    ? fallbackRetailers
    : [
        { name: "Google Shopping", url: `https://www.google.com/search?tbm=shop&q=${encodeURIComponent(cleanQuery)}`, in_person: false },
        { name: "Zara",            url: `https://www.zara.com/us/en/search?searchTerm=${encodeURIComponent(cleanQuery)}`, in_person: true },
        { name: "H&M",             url: `https://www2.hm.com/en_us/search-results.html?q=${encodeURIComponent(cleanQuery)}`, in_person: true },
        { name: "ASOS",            url: `https://www.asos.com/us/search/?q=${encodeURIComponent(cleanQuery)}`, in_person: false },
        { name: "Nordstrom",       url: `https://www.nordstrom.com/sr?keyword=${encodeURIComponent(cleanQuery)}`, in_person: true },
        { name: "Amazon",          url: `https://www.amazon.com/s?k=${encodeURIComponent(cleanQuery)}`, in_person: false },
      ];

  return (
    <div className="rsf" data-testid={testId || "real-finds"}>
      <div className="rsf-header">
        <ShoppingBag size={12} strokeWidth={1.8} />
        <span>{sectionLabel || (hasRealProducts ? "SHOP THIS LOOK" : "WHERE TO BUY")}</span>
      </div>

      {hasRealProducts && (
        <div className="rsf-grid">
          {products.slice(0, limit).map((p, i) => (
            <ProductCard
              key={i}
              product={p}
              dt={testId ? `${testId}-${i}` : `real-find-${i}`}
            />
          ))}
        </div>
      )}

      {showFallback && !hasRealProducts && (
        <div className="rsf-grid">
          {fallbacks.slice(0, 6).map((r, i) => (
            <FallbackCard
              key={r.name + i}
              retailer={r}
              query={cleanQuery}
              dt={testId ? `${testId}-fallback-${i}` : `real-find-fallback-${i}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
